/**
 * Admin Authentication Hook
 * Handles admin user provisioning after Better Auth sign-in.
 *
 * Pattern matches internal app's useAuth hook:
 * - After sign-in + page reload, Convex auth token is established
 * - This hook detects when a Better Auth session exists but no admin user is linked
 * - It calls provisionFromAuth to link/verify the admin user record
 */
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { useEffect, useRef, useState } from "react";
import { useBetterAuthSession } from "../lib/auth";

export function useAdminAuth() {
  // Better Auth session (from HTTP cookie, always available after sign-in)
  const { data: betterAuthSession, isPending: sessionPending } =
    useBetterAuthSession();

  // Convex admin session (requires JWT auth to be established)
  const adminSession = useQuery(api.admin.auth.getSession);

  // Provision mutation
  const provisionFromAuth = useMutation(api.admin.auth.provisionFromAuth);
  const provisioningRef = useRef(false);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [provisioningFailed, setProvisioningFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset state when session user changes
  useEffect(() => {
    provisioningRef.current = false;
    setIsProvisioning(false);
    setProvisioningFailed(false);
    setErrorMessage(null);
  }, [betterAuthSession?.user?.id]);

  // Clear provisioning state once admin user is found
  useEffect(() => {
    if (adminSession?.adminUser) {
      provisioningRef.current = false;
      setIsProvisioning(false);
      setProvisioningFailed(false);
      setErrorMessage(null);
    }
  }, [adminSession?.adminUser]);

  // Auto-provision: when Better Auth session exists + Convex auth is ready
  // but no admin user linked yet
  useEffect(() => {
    if (
      adminSession !== undefined && // Convex query resolved (auth token is working)
      adminSession?.user && // Convex auth user exists
      !adminSession?.adminUser && // No admin user linked yet
      !provisioningRef.current &&
      !provisioningFailed
    ) {
      provisioningRef.current = true;
      setIsProvisioning(true);
      console.log(
        "[useAdminAuth] Provisioning admin user for:",
        adminSession.user.email
      );
      provisionFromAuth({
        authUserId: adminSession.user.id,
        email: adminSession.user.email,
        name: adminSession.user.name || adminSession.user.email.split("@")[0],
      })
        .then((adminId) => {
          console.log("[useAdminAuth] Admin user provisioned:", adminId);
          setIsProvisioning(false);
        })
        .catch((err) => {
          console.error("[useAdminAuth] Provisioning failed:", err);
          provisioningRef.current = false;
          setIsProvisioning(false);
          setProvisioningFailed(true);
          const msg =
            err instanceof Error ? err.message : String(err);
          if (msg.includes("Admin account is not active")) {
            setErrorMessage("inactive");
          } else {
            setErrorMessage("not_authorized");
          }
        });
    }
  }, [adminSession, provisionFromAuth, provisioningFailed]);

  const isPending =
    sessionPending ||
    adminSession === undefined ||
    isProvisioning;

  return {
    session: adminSession,
    isPending,
    isAuthenticated:
      !!adminSession?.adminUser && !provisioningFailed,
    isSuperAdmin: adminSession?.adminUser?.role === "super_admin",
    isAdministrator: adminSession?.adminUser?.role === "administrator",
    organizationId: adminSession?.adminUser?.organizationId,
    organizationName: adminSession?.adminUser?.organizationName,
    provisioningFailed,
    errorMessage,
  };
}
